import { useContext, useMemo } from 'react'
import AuthorizationContext from '../context/AuthorizationContext'

export enum Role {
    Administrator = 'Hammock.Administrator',
    Access = 'Hammock.Access', //The default role. A user must have at least one role.
    TaskAll = 'Hammock.Task.All',
    TaskFileOwner = 'Hammock.Task.File.Owner',
    TaskOwner = 'Hammock.Task.Owner',
    TimeAll = 'Hammock.Time.All',
    TimeFileOwner = 'Hammock.Time.File.Owner',
    TimeFileTracked = 'Hammock.Time.File.Tracked',
    TimeOwner = 'Hammock.Time.Owner',
    Invoice = 'Hammock.Invoice',
    Report = 'Hammock.Report',
    FileLimited = 'Hammock.File.Limited',
}

//These roles allow the user to read (their own) tasks
export const rolesTasks = [Role.TaskAll, Role.TaskFileOwner, Role.TaskOwner]

//These roles allow the user to read and write all tasks
export const rolesWriteAllTask = [Role.TaskAll, Role.TaskFileOwner]

//These roles allow the user to read (their own) times
export const rolesTimes = [Role.TimeAll, Role.TimeFileOwner, Role.TimeFileTracked, Role.TimeOwner]

//These roles allow the user to read all times but not write them
export const rolesReadAllTimes = [Role.TimeAll, Role.TimeFileOwner, Role.TimeFileTracked]

//These roles allow the user to read and write all times
export const rolesWriteAllTimes = [Role.TimeAll, Role.TimeFileOwner]

type ReturnProps = {
    loaded: boolean
    all: (needle: Role[]) => boolean // User has all the roles in the needle or is an administrator.
    any: (needle: Role[]) => boolean // User has any of the roles in the needle or is an administrator.
    only: (needle: Role[]) => boolean // User has only one of the roles in the needle.
    none: (needle: Role[]) => boolean // User has none of the roles in the needle.
}

export default function useRoles(): ReturnProps {
    const { roles } = useContext(AuthorizationContext)
    const loaded = useMemo(() => roles?.length > 0, [roles])

    return {
        loaded,
        all: (needle: Role[]) => roles.includes(Role.Administrator) || needle.map(role => roles.includes(role)).reduce((a, b) => a && b),
        any: (needle: Role[]) => [Role.Administrator, ...needle].map(role => roles.includes(role)).reduce((a, b) => a || b),
        only: (needle: Role[]) => needle.map(role => roles.includes(role)).reduce((a, b) => a || b),
        none: (needle: Role[]) => !needle.map(role => roles.includes(role)).reduce((a, b) => a || b),
    }
}
